import { DNADivider } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';
import React, { useCallback, useEffect, useRef } from 'react'
import { FlatList, Pressable } from 'react-native';
import SearchResultsItem from './SearchResultsItem';
import { MatchedPage } from 'src/hooks/useFileTextSearch/useFileTextSearch';

interface SearchResultsContentProps {
    matches: MatchedPage[],
    searchQuery: string,
    activeIndex: number,
    setActiveIndex: (index: number) => void,
    setActiveSlide: (slide:number) => void,
  }

const SearchResultsContent: React.FC<SearchResultsContentProps> = React.memo((
  { matches, searchQuery, activeIndex, setActiveIndex, setActiveSlide }) => {
  const flatlistRef = useRef<FlatList<MatchedPage> | null>(null);
  const matchesLengthRef = useRef(matches.length);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retriedIndexesRef = useRef(new Set<number>());
  matchesLengthRef.current = matches.length;

  const scrollToIndex = useCallback((index: number) => {
    if (index < 0 || index >= matchesLengthRef.current) return;

    flatlistRef.current?.scrollToIndex({ animated: true, index });
  }, []);

  const handleScrollToIndexFailed = useCallback(({
    averageItemLength,
    index,
  }: {
    averageItemLength: number,
    index: number,
  }) => {
    flatlistRef.current?.scrollToOffset({
      animated: false,
      offset: averageItemLength * index,
    });

    if (retriedIndexesRef.current.has(index)) return;

    retriedIndexesRef.current.add(index);
    retryTimeoutRef.current = setTimeout(() => {
      scrollToIndex(index);
    }, 0);
  }, [scrollToIndex]);

  useEffect(() => {
    retriedIndexesRef.current.clear();
  }, [matches]);

  useEffect(() => {
    if (matches.length && activeIndex >= matches.length) {
      setActiveIndex(matches.length - 1);
    }
  }, [activeIndex, matches.length, setActiveIndex]);

  useEffect(() => () => {
    if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (flatlistRef.current && matches.length && activeIndex > -1) {
      scrollToIndex(activeIndex);
    }
  }, [activeIndex, matches.length, scrollToIndex]);

  return (
    <FlatList
      data={matches}
      keyExtractor={(item) => `slide-${item.presentationPageNumber}`}
      renderItem={({ item, index }) => {
        return (
          <Pressable
            onPress={() => {
              setActiveIndex(index);
              setActiveSlide(item.presentationPageNumber)
            }}
            style={{ backgroundColor : activeIndex === index ? colors['color-gray-80'] : undefined }}
          >
            <SearchResultsItem item={item} searchQuery={searchQuery} />
          </Pressable>
        )
      }}
      style={{ height: 300, overflow: 'scroll' }}
      ref={flatlistRef}
      ItemSeparatorComponent={DNADivider}
      onScrollToIndexFailed={handleScrollToIndexFailed}
    />
  )
})

export default SearchResultsContent
